import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';
import { getHostedAppUrls } from '../config/hostUrls';

function buildQrUrl(value, version) {
  if (!value) return '';
  const cacheKey = version ? `&v=${encodeURIComponent(version)}` : '';
  return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(value)}${cacheKey}`;
}

function formatTimestamp(value) {
  if (!value) return 'No activity yet';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function statusLabel(entry) {
  if (!entry) return 'Offline';
  if (entry.status === 'running') return 'Live';
  if (entry.status === 'starting') return 'Starting';
  if (entry.status === 'error') return 'Error';
  return 'Offline';
}

function statusClass(entry) {
  if (!entry) return 'sa-badge-inactive';
  if (entry.status === 'running') return 'sa-badge-active';
  if (entry.status === 'starting') return 'sa-badge-trial';
  if (entry.status === 'error') return 'sa-badge-suspended';
  return 'sa-badge-inactive';
}

function PreviewCard({ label, appKey, entry, fallbackWebUrl }) {
  const qrTarget = entry?.qrTarget || entry?.expoGoUrl || entry?.shareUrl || '';
  const webUrl = entry?.webUrl || fallbackWebUrl;
  const publicUrl = entry?.publicUrl || '';
  const updatedAt = entry?.updatedAt || '';

  return (
    <div className="expo-preview-card">
      <div className="expo-preview-card-header">
        <div>
          <div className="expo-preview-role">{appKey}</div>
          <h3>{label}</h3>
        </div>
        <span className={`sa-badge ${statusClass(entry)}`}>{statusLabel(entry)}</span>
      </div>

      <div className="expo-preview-links">
        <div className="expo-preview-link-row">
          <span>Expo Go</span>
          {qrTarget ? (
            <a href={qrTarget} target="_blank" rel="noreferrer">{qrTarget}</a>
          ) : (
            <span className="expo-preview-empty">Waiting for preview URL…</span>
          )}
        </div>
        <div className="expo-preview-link-row">
          <span>Preview Host</span>
          {publicUrl ? (
            <a href={publicUrl} target="_blank" rel="noreferrer">{publicUrl}</a>
          ) : (
            <span className="expo-preview-empty">Not configured</span>
          )}
        </div>
        <div className="expo-preview-link-row">
          <span>Web Fallback</span>
          <a href={webUrl} target="_blank" rel="noreferrer">{webUrl}</a>
        </div>
      </div>

      <div className="expo-preview-warning">
        Use only the preview Expo Go link or QR for this app.
        The browser fallback URLs will not work inside Expo Go.
      </div>

      <div className="expo-preview-meta">
        <span>Updated {formatTimestamp(updatedAt)}</span>
        {entry?.lastLine ? <span className="expo-preview-last-line">{entry.lastLine}</span> : null}
      </div>

      <div className="expo-preview-qr-block">
        {qrTarget ? (
          <img src={buildQrUrl(qrTarget, updatedAt)} alt={`${label} Expo Go QR`} className="expo-preview-qr" />
        ) : (
          <div className="expo-preview-qr expo-preview-qr-placeholder">QR will appear after the preview server starts.</div>
        )}
      </div>

      <div className="expo-preview-log">
        <strong>Recent runner output</strong>
        {entry?.logs?.length ? (
          <div className="expo-preview-log-list">
            {entry.logs.slice().reverse().map((log, index) => (
              <div key={`${log.ts}-${index}`}>
                <span>{formatTimestamp(log.ts)}</span> {log.line}
              </div>
            ))}
          </div>
        ) : (
          <p className="expo-preview-empty">No runner logs yet.</p>
        )}
      </div>
    </div>
  );
}

export default function ExpoPreviews({ token, navigate }) {
  const [data, setData] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const hostedAppUrls = getHostedAppUrls();

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const result = await apiFetch('/dev-previews', {}, token);
        if (!active) return;
        setData(result.data || {});
        setError('');
      } catch (err) {
        if (!active) return;
        setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    const intervalId = setInterval(load, 10000);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [token]);

  const previews = useMemo(() => ([
    { key: 'staff', label: 'Staff Expo Preview', entry: data.staff || null },
    { key: 'parent', label: 'Parent Expo Preview', entry: data.parent || null },
  ]), [data]);

  return (
    <div>
      <div className="expo-preview-page-header">
        <div>
          <button className="sa-btn" onClick={() => navigate('dashboard')}>← Back</button>
          <h2>Expo Preview Hub</h2>
          <p>
            Live Expo Go preview links for real-device testing. Use only the `parent-preview` and `staff-preview` QR codes or Expo Go links below.
          </p>
        </div>
      </div>

      {error && <div className="sa-error">{error}</div>}

      <div className="sa-card expo-preview-summary">
        <div>
          <strong>How to use</strong>
          <p>1. Open Expo Go on the device.</p>
          <p>2. Scan only the QR shown on this page, or open the exact Expo Go link directly.</p>
          <p>3. Ignore any older preview links already saved in Expo Go history.</p>
          <p>4. The web fallback domains are browser-only and will fail inside Expo Go.</p>
        </div>
      </div>

      {loading ? (
        <div className="sa-empty">Loading Expo preview state…</div>
      ) : (
        <div className="expo-preview-grid">
          {previews.map((preview) => (
            <PreviewCard
              key={preview.key}
              label={preview.label}
              appKey={preview.key}
              entry={preview.entry}
              fallbackWebUrl={hostedAppUrls[preview.key]}
            />
          ))}
        </div>
      )}
    </div>
  );
}
